import { MouseEvent } from 'react'
import { styled } from '@mui/material/styles'
import { colorPalette } from '../../style/colorPalette'
import { open } from '../../router'
import { WbsItemIcon } from '../../components/icons/WbsItemIcon'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'
import { useWbsItemBasicByCode } from './hooks/useWbsItemBasicByCode'
import { WbsItemHoverCard } from './WbsItemHoverCard'

// WbsItemAttachmentChip の見た目（白地に細い枠）を踏襲しつつ、本文中に混ざるインライン要素として
// 成立させる。添付チップは入力欄・送信済みバブル専用のブロック要素のため流用できない。
// フォントサイズは em 指定にして、本文（MarkdownContent）側の文字サイズにそのまま追従させる。
const ChipRoot = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  verticalAlign: 'middle',
  maxWidth: '100%',
  padding: '0 6px',
  borderRadius: 4,
  border: `1px solid ${colorPalette.monotone[2]}`,
  backgroundColor: colorPalette.monotone[0],
  color: colorPalette.monotone[10],
  fontSize: '0.92em',
  fontWeight: 600,
  lineHeight: 1.6,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
    textDecoration: 'none',
  },
})

// 取得中はアイコンが無く、取得後に現れる。幅を先に確保しておき、その出現でラベルの位置が
// 左右にずれる（レイアウトシフトする）のを避ける。
const IconSlot = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 14,
  height: 14,
  flexShrink: 0,
})

const TypeIcon = styled(WbsItemIcon)({
  width: 14,
  height: 14,
  display: 'block',
})

const Label = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

export type WbsItemLinkChipProps = {
  code: string
  label: string
}

/**
 * 応答本文の Markdown リンク `[名称](wbs:コード)` をチップ表示に差し替えるコンポーネント。
 *
 * ラベルは本文に書かれた名称をそのまま表示する（モデルが文脈に合わせて短縮した名称を書いて
 * いる場合があり、本文の文字が取得結果で勝手に変わると驚きがあるため）。一方でアイコンは
 * 種別が分からないと出せない（誤った推測で出すと誤情報になる）ため、コードから実体を取得して
 * 正しい種別のアイコンを出す。取得・キャッシュは useWbsItemBasicByCode に任せる。
 *
 * 取得できない（404 = モデルの幻覚、403 = 参照権限なし）場合は、実在するかのような見た目の
 * チップを出さず本文と同じプレーンテキストにする（useWbsItemBasicByCode 参照）。
 *
 * href に実パスを入れた a 要素として描画することで、中クリックや右クリック→新しいタブで開く、
 * はブラウザ標準の挙動でそのまま実現できる。通常クリックだけ preventDefault して SPA 内遷移に
 * 差し替える（ctrl/meta クリックでの新規タブ化は open() 自身が event から判定する）。
 *
 * ホバー時のカード（WbsItemHoverCard）は basic（名称・種別・プロジェクトコード）が
 * 取得できてから付ける。まだ loading の間は本文の名称しかなく、出しても得るものがないため。
 * ネイティブの title 属性は使わない（ホバーカードと二重に出て邪魔になるため廃止）。
 */
export const WbsItemLinkChip = ({ code, label }: WbsItemLinkChipProps) => {
  const lookup = useWbsItemBasicByCode(code)

  // 取得できなかったコードをそれらしいチップで見せてはいけない（モデルの幻覚の可能性がある、
  // または参照権限がなく本当は中身を確認できない）。地の文と区別のつかないプレーンテキストにする。
  if (lookup.status === 'unavailable') {
    return <>{label}</>
  }

  const path = `/wbsItem/${code}`
  const wbsItemType =
    lookup.status === 'resolved'
      ? new WbsItemTypeVO(lookup.basic.typeDto)
      : undefined
  // 表示名は取得できた正式名称を優先する。エージェントがリンクテキストにコードを書いてしまう
  // ことがあり（8桁の英数字がそのまま本文に出ると何のアイテムか読み取れない）、常に名称が
  // 出るようにするため。取得できるまでは本文に書かれた文字で代用する。
  const displayName =
    lookup.status === 'resolved' ? lookup.basic.displayName : label

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    // 第3引数の title は window.open のウィンドウ名になる（同名のタブが再利用される）。
    // 名称を渡す意図はないため既存の遷移箇所と同じく省略する。
    open(path, event)
  }
  const chip = (
    // data-wbs-item-link-chip は MarkdownContent 側の汎用リンクスタイル（'& a'）から
    // このチップを除外するための目印。属性なので styled() の specificity を上げずに済む。
    <ChipRoot href={path} onClick={handleClick} data-wbs-item-link-chip="">
      <IconSlot>{wbsItemType && <TypeIcon type={wbsItemType} />}</IconSlot>
      <Label>{displayName}</Label>
    </ChipRoot>
  )
  return lookup.status === 'resolved' ? (
    <WbsItemHoverCard code={code} basic={lookup.basic}>
      {chip}
    </WbsItemHoverCard>
  ) : (
    chip
  )
}
